import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {
  ScheduleType,
  ScheduleNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setScheduleTypeInputSchema = z.object({
  scheduleType: z
    .nativeEnum(ScheduleType)
    .describe('The schedule type (Date range or Days of week)'),
});

export default function setScheduleTypeTool() {
  async function execute(
    params: ToolExecuteParams<typeof setScheduleTypeInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {scheduleType} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ScheduleCardScratchpadState>(
      agentScratchpad,
      NodeType.SCHEDULE,
    );

    const previousType = validatedScratchpad.card.scheduleType;
    const regularOutputsCount = validatedScratchpad.card.outputs.filter(
      (output) => output.type === OutputType.Regular,
    ).length;

    validatedScratchpad.card.scheduleType = scheduleType;

    if (previousType !== scheduleType) {
      const fallbackOutput = validatedScratchpad.card.outputs.find(
        (output) => output.type === OutputType.Fallback,
      );
      validatedScratchpad.card.outputs = (
        fallbackOutput
          ? [fallbackOutput]
          : validatedScratchpad.card.outputs.filter((o) => o.type === OutputType.Fallback)
      ) as ScheduleNodeOutputData[];

      if (regularOutputsCount > 0) {
        return toolResult(
          `Schedule type set to ${scheduleType}. All ${regularOutputsCount} regular output(s) were deleted because they don't match the new schedule type.`,
        );
      }
    }

    return toolResult(`Schedule type set to ${scheduleType}`);
  }

  return tool<typeof setScheduleTypeInputSchema, ScheduleCardScratchpadState>({
    name: V2CardAgentToolName.SetScheduleType,
    displayName: 'Setting schedule type',
    description: 'Set schedule type and clear regular outputs on change.',
    inputSchema: setScheduleTypeInputSchema,
    execute,
  });
}
